import $ from "jquery";
import {Component, h} from "preact";
import {
	DAYS_PER_MONTH,
	EVERY_12_MONTHS,
	EVERY_2_WEEKS,
	EVERY_3_MONTHS,
	EVERY_4_WEEKS,
	EVERY_6_MONTHS,
	EVERY_MONTH,
	EVERY_WEEK,
	formatAmount,
	formatPrice,
} from "../../lib/price";
import {formatDate} from "mojave/date/format";
import {SELECTION_ALL} from "./Options/OptionsSelection";
import {isNextMonth, parseDate} from "../../lib/date";

const INTERVAL_ONCE = 0;
const INTERVAL_LABEL_PREFIXES = {
	[INTERVAL_ONCE]: "Einmaliger",
	[EVERY_WEEK]: "Wöchentlicher",
	[EVERY_2_WEEKS]: "Zweiwöchentlicher",
	[EVERY_4_WEEKS]: "Vierwöchentlicher",
	[EVERY_MONTH]: "Monatlicher",
	[EVERY_3_MONTHS]: "Quartalsmäßiger",
	[EVERY_6_MONTHS]: "Halbjährlicher",
	[EVERY_12_MONTHS]: "Jährlicher",
};

/**
 * @typedef {{interval: number, amount: number}[]} OverviewStepContent.Totals
 *
 *
 * @typedef {{
 *      selectedRuntime: number,
 *      firstDay: string|null,
 *      selectedOptions: Object<number, Object<number, boolean>>,
 *      formData: Membership.FormData,
 *      contract: Membership.Contract,
 *      onProceed: function(number),
 *      privacyPageUrl: ?string,
 * }} OverviewStepContent.Props
 *
 * @typedef {{
 *      privacyConfirmMissing: boolean,
 * }} OverviewStepContent.State
 */
export class OverviewStepContent extends Component
{
	/**
	 * @inheritDoc
	 */
	constructor (props)
	{
		super(props);
		this.state = {
			privacyConfirmMissing: false,
		};
	}


	/**
	 * @param {OverviewStepContent.Props} props
	 * @param {OverviewStepContent.State} state
	 * @returns {null|JSX.Element}
	 */
	render (props, state)
	{
		let runtime = this.getSelectedRuntime(props.contract.runtimes, props.selectedRuntime);
		let today = new Date();

		const openingOfGymDate = parseDate(this.props.firstDay);
		let startOfContract = parseDate(props.formData.contractStart) || new Date();

		// neat trick: go to next month and one day back
		let numbersOfDaysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
		let daysBeforeStart = numbersOfDaysInMonth - today.getDate();

		// if the gym opens on a given date, then we can't be earlier than that
		if (openingOfGymDate && (openingOfGymDate.getTime() >= startOfContract.getTime()))
		{
			startOfContract = openingOfGymDate;
			daysBeforeStart = 0;
		}
		else if (!isNextMonth(startOfContract))
		{
			daysBeforeStart = 0;
		}

		let daysBeforeStartLabel = daysBeforeStart === 1 ? "Tag" : "Tage";
		let hasEarlyStart = 0 < daysBeforeStart;
		let startOfContractDate = startOfContract ? formatDate(startOfContract) : null;

		// get totals
		let totals = this.getTotals(props.contract, runtime, props.selectedOptions);

		// get pre payment amount
		let earlyStartAmount = hasEarlyStart
			? this.getPrePaymentAmount(daysBeforeStart, totals)
			: 0;

		return (
			<div class="_membership-overview">
				<div class="_membership-headline">Bestätige Deine Angaben</div>
				<div class="_membership-overview-topics">
					<div class="_membership-overview-topic">
						<div class="_membership-overview-section">
							<div class="_membership-section-headline">Vertragspartner</div>
							<ul class="_membership-overview-values">
								<li>{this.buildName(props.formData)}</li>
								<li>{props.formData.street}</li>
								<li>{`${props.formData.areaCode} ${props.formData.city}`}</li>
								<li>{`Geburtstag: ${props.formData.birthday}`}</li>
								{props.formData.phone !== null && (
									<li>{`Telefon: ${props.formData.phone}`}</li>
								)}
								<li>{`Mobil: ${props.formData.mobile}`}</li>
								<li>{props.formData.email}</li>
							</ul>
						</div>
						{null !== props.formData.presenteeActive && (
							<div class="_membership-overview-section">
								<div class="_membership-section-headline">Vertragsnutzer</div>
								<ul class="_membership-overview-values">
									<li>{props.formData.presenteeName}</li>
									<li>{`Geburtstag: ${props.formData.presenteeBirthday}`}</li>
									<li>{props.formData.presenteeStreet}</li>
									<li>{`${props.formData.presenteeAreaCode} ${props.formData.presenteeCity}`}</li>
									<li>{props.formData.presenteeEmail}</li>
								</ul>
							</div>
						)}
						<div class="_membership-overview-section">
							<div class="_membership-section-headline">Kontoinformationen</div>
							<ul class="_membership-overview-values">
								<li>{props.formData.accountHolder}</li>
								<li>{`IBAN: ${props.formData.iban}`}</li>
								<li>{`BIC: ${props.formData.bic}`}</li>
							</ul>
						</div>
					</div>
					<div class="_membership-overview-topic">
						<div class="_membership-overview-section">
							<div class="_membership-section-headline">Vertrag</div>
							<ul class="_membership-overview-values">
								<li>{runtime.title}</li>
								<li>{`Mitgliedbeitrag monatlich: ${formatAmount(runtime.recurringFee)}`}</li>
								<li class="_membership-overview-value-help">Ab Mitgliedsbeginn monatlich</li>
								<li>{`Startpaket: ${formatAmount(runtime.entryFee)}`}</li>
								<li class="_membership-overview-value-help">Bei Mitgliedsbeginn einmalig</li>
							</ul>
						</div>
						{this.renderOptions(props.contract.optionsCategories, props.selectedOptions)}
						<div class="_membership-overview-section">
							<div class="_membership-section-headline">Vertragsinformationen</div>
							<ul class="_membership-overview-values">
								<li>{`Vertragsbeginn: ${startOfContractDate}`}</li>
								{daysBeforeStart > 0 && (
									<li>{`Anteilig berechnet: ${daysBeforeStart} ${daysBeforeStartLabel}`}</li>
								)}
							</ul>
						</div>
						<div class="_membership-overview-section">
							<div class="_membership-section-headline">Abrechnungsübersicht</div>
							<ul class="_membership-overview-values">
								{totals.map(entry => {
									const result = [
										<li>{`${INTERVAL_LABEL_PREFIXES[entry.interval]} Abbuchungsbetrag: ${formatAmount(entry.amount)}`}</li>,
									];

									if (entry.interval > 0 && entry.interval < DAYS_PER_MONTH)
									{
										result.push(<li class="_membership-overview-value-help">Wird gesammelt mit dem Monatsbeitrag abgebucht.</li>);
									}

									return result;
								})}
								{(hasEarlyStart && 0 < earlyStartAmount) && (
									<li class="_membership-overview-value-separated">{`Anteilig berechnet: ${formatAmount(earlyStartAmount)}`}</li>
								)}
								{(hasEarlyStart && 0 < earlyStartAmount) && (
									<li class="_membership-overview-value-help">{`Es wurden bis zum Vertragsbeginn am ${startOfContractDate} einmalig ${daysBeforeStart} ${daysBeforeStartLabel} anteilig berechnet.`}</li>
								)}
							</ul>
						</div>
					</div>
				</div>
				{props.privacyPageUrl !== null && (
					<label class="_membership-privacy-confirm">
						<input type="checkbox" />
						Ich bin mit den <a href={props.privacyPageUrl} target="_blank" rel="noreferrer">Datenschutzbestimmungen</a> einverstanden.
						{state.privacyConfirmMissing && (
							<span class="_membership-privacy-confirm-missing">Sie müssen den Datenschutzbestimmungen zustimmen.</span>
						)}
					</label>
				)}
				<div class="_membership-proceed">
					<button type="button" class="_membership-proceed-button" onClick={() => this.onProceed(this.getMonthlyFee(totals))}>Zahlungspflichtigen Vertrag abschließen</button>
				</div>
			</div>
		);
	}


	/**
	 * Checks whether the privacy terms were accepted
	 *
	 * @param {string} startOfContractDate
	 * @param {number} monthlyFee
	 */
	onProceed (monthlyFee)
	{
		/** @type {OverviewStepContent.Props} props */
		const props = this.props;

		const $checkbox = $(this.base).find("._membership-privacy-confirm input:checkbox");

		if ($checkbox.length > 0 && !$checkbox.prop("checked"))
		{
			this.setState({privacyConfirmMissing: true});
		}
		else
		{
			this.setState({privacyConfirmMissing: false});
			props.onProceed(monthlyFee);
		}
	}


	/**
	 * Renders the selected options
	 *
	 * @private
	 * @param {Membership.OptionsCategory[]} optionsCategories
	 * @param {Object<number, Object<number, boolean>>} selectedOptions
	 * @returns {JSX.Element[]}
	 */
	renderOptions (optionsCategories, selectedOptions)
	{
		const renderedCategories = [];

		optionsCategories.forEach(
			category =>
			{
				let renderedOptions = [];

				category.options.forEach(
					option =>
					{
						// in a category with "all selected" every option is selected.
						// If it is a different selection method, check whether the user has selected the option
						let isSelected = category.selectionMethod === SELECTION_ALL || (selectedOptions[category.id] !== undefined && selectedOptions[category.id][option.id] === true);

						if (isSelected)
						{
							const formattedPrice = formatPrice(option.price);
							renderedOptions.push(formattedPrice !== null
								? <li>{`${option.name} (${formattedPrice})`}</li>
								: <li>{option.name}</li>
							);
						}
					}
				);

				if (renderedOptions.length > 0)
				{
					renderedCategories.push(
						<div class="_membership-overview-section">
							<div class="_membership-section-headline">{category.headline}</div>
							<ul class="_membership-overview-values">
								{renderedOptions}
							</ul>
						</div>
					);
				}
			}
		);

		return renderedCategories;
	}


	/**
	 * Calculates an approximate monthly fee
	 *
	 * @private
	 * @param {OverviewStepContent.Totals} totals
	 * @returns {number}
	 */
	getMonthlyFee (totals)
	{
		let amount = 0;

		totals.forEach(
			entry => {
				if (entry.interval > 0)
				{
					amount += entry.amount * (DAYS_PER_MONTH / entry.interval);
				}
			}
		);

		return Math.round(amount);
	}


	/**
	 *
	 * @param {Membership.Contract} contract
	 * @param {Membership.Runtime} selectedRuntime
	 * @param {Object<number, Object<number>>} selectedOptions
	 *
	 * @returns {OverviewStepContent.Totals}
	 */
	getTotals (contract, selectedRuntime, selectedOptions)
	{
		const totals = {
			[INTERVAL_ONCE]: 0,
			[EVERY_WEEK]: 0,
			[EVERY_2_WEEKS]: 0,
			[EVERY_4_WEEKS]: 0,
			[EVERY_MONTH]: 0,
			[EVERY_3_MONTHS]: 0,
			[EVERY_6_MONTHS]: 0,
			[EVERY_12_MONTHS]: 0,
		};

		// add runtime costs
		totals[INTERVAL_ONCE] += selectedRuntime.entryFee;
		totals[EVERY_MONTH] += selectedRuntime.recurringFee;

		// add
		contract.optionsCategories.forEach(
			category =>
			{
				category.options.forEach(
					option =>
					{
						// the option is selected, if either the category is a "select all" category or the user has selected the option
						let isSelected = (category.selectionMethod === SELECTION_ALL) || (selectedOptions[category.id] !== undefined && selectedOptions[category.id][option.id] === true);

						// only get elements that are selected and the option has a price
						if (isSelected && option.price !== null)
						{
							totals[option.price.interval] += option.price.amount;
						}
					}
				);
			}
		);

		// transform return value
		let returnValue = [];
		for (const interval in totals)
		{
			if (!totals.hasOwnProperty(interval) || totals[interval] === 0)
			{
				continue;
			}

			returnValue.push({
				interval: parseInt(interval, 10),
				amount: totals[interval],
			});
		}

		return returnValue;
	}


	/**
	 * Returns the partial payment for the first month
	 *
	 * @private
	 * @param {number} daysBeforeStart
	 * @param {OverviewStepContent.Totals} totals
	 * @returns {number}
	 */
	getPrePaymentAmount (daysBeforeStart, totals)
	{
		let prePaymentAmount = 0;

		totals.forEach(
			entry =>
			{
				// skip if the interval is not at least monthly
				if (entry.interval > 0)
				{
					prePaymentAmount += entry.amount * (daysBeforeStart / entry.interval);
				}
			}
		);

		return Math.round(prePaymentAmount);
	}


	/**
	 * Returns the selected runtime
	 *
	 * @param {Membership.Runtime[]} runtimes
	 * @param {number} runtimeId
	 * @returns {?Membership.Runtime}
	 */
	getSelectedRuntime (runtimes, runtimeId)
	{
		for (let i = 0; i < runtimes.length; i++)
		{
			if (runtimes[i].id === runtimeId)
			{
				return runtimes[i];
			}
		}

		return null;
	}


	/**
	 * Builds the name from the form data
	 *
	 * @private
	 * @param {Membership.FormData} formData
	 * @returns {string}
	 */
	buildName (formData)
	{
		let salutation = formData.salutation === "m" ? "Herr" : "Frau";

		switch (formData.title)
		{
		case "dr":
			salutation += " Dr.";
			break;

		case "prof":
			salutation += " Prof.";
			break;

		case "prof_dr":
			salutation += " Prof. Dr.";
			break;
		}

		return `${salutation} ${formData.firstName} ${formData.lastName}`;
	}
}
